import React, { useState, useEffect, useCallback } from "react";
import { req } from "../../utils";
import { Grid, Typography } from "@mui/material/";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import fileContractIcon from '../../assets/images/file-contract-regular.svg';
import { differenceInCalendarDays } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatNumber = (number) => {
  try {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  } catch (e) {
    return number;
  }
};

const safeNumber = (val) => {
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

export const Contracts = ({ filterProps }) => {
  const { dateRanges, pageFilterByType } = filterProps;
  const [applicationData, setApplicationData] = useState(null);
  const [updateDate, setUpdateDate] = useState(new Date());
  // Default to last 30 days if no date range is provided
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const startDate = dateRanges[0] ? Math.floor(new Date(dateRanges[0]).getTime() / 1000) : Math.floor(thirtyDaysAgo.getTime() / 1000);
  const endDate = dateRanges[1] ? Math.floor(new Date(dateRanges[1]).getTime() / 1000) : Math.floor(Date.now() / 1000);
  const fetchApplicationsData = useCallback(async () => {
    const url = `get_customer_application_insights?s=${startDate}&e=${endDate}`;
    try {
      const data = await req("GET", url);
      setApplicationData(data);
      setUpdateDate(new Date());
    } catch (error) {
      console.error("Error fetching application data:", error);
    }
  }, [dateRanges]);

  useEffect(() => {
    fetchApplicationsData();
  }, [fetchApplicationsData]);

  const isVisible = pageFilterByType === 'unfiltered' || pageFilterByType === 'contract';

  if (!applicationData) return null;

  // Filter and aggregate approvals_by_date based on date range and granularity
  let chartLabels = [];
  let chartCounts = [];
  if (applicationData && applicationData.approvals_by_date) {
    // Filter to selected date range
    const filteredApprovals = applicationData.approvals_by_date.filter(item => {
      const date = new Date(item.approval_date);
      return (!dateRanges[0] || date >= new Date(dateRanges[0])) &&
             (!dateRanges[1] || date <= new Date(dateRanges[1]));
    });
    const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
    if (days > 31) {
      // Aggregate by month
      const monthMap = {};
      filteredApprovals.forEach(item => {
        const month = item.approval_date.slice(0, 7); // 'YYYY-MM'
        monthMap[month] = (monthMap[month] || 0) + parseInt(item.approval_count, 10);
      });
      chartLabels = Object.keys(monthMap).sort();
      chartCounts = chartLabels.map(month => monthMap[month]);
    } else {
      // Use daily
      chartLabels = filteredApprovals.map(item => item.approval_date);
      chartCounts = filteredApprovals.map(item => parseInt(item.approval_count, 10));
    }
  }

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'Approvals',
        data: chartCounts,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Approvals by Date',
      },
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: (() => {
            if (dateRanges && dateRanges.length === 2) {
              const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
              return days > 31 ? 'month' : 'day';
            }
            return 'day';
          })()
        }
      }
    }
  };

  return (
    <Grid item xs={12} className={isVisible ? '' : 'd-none'}>
      <div className="card card-stat-summary">
        <div className="card-body">
          <div className="card-stat-summary--title">Contract Insights</div>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <div className="contract-stat">
                <div className="contract-stat-value">{formatNumber(safeNumber(applicationData.total_contracts))}</div>
                <div className="contract-stat-label">Total Contracts</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="contract-stat">
                <div className="contract-stat-value">{formatNumber(safeNumber(applicationData.renewals))}</div>
                <div className="contract-stat-label">Renewals</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="contract-stat">
                <div className="contract-stat-value">{formatNumber(safeNumber(applicationData.new_contracts))}</div>
                <div className="contract-stat-label">New Contracts</div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className="contract-stat">
                <div className="contract-stat-value">{formatNumber(Math.round(safeNumber(applicationData.overall_avg_contract_length_in_days)))}</div>
                <div className="contract-stat-label">Avg Contract Length (days)</div>
              </div>
            </Grid>
          </Grid>
          
          <div style={{ height: '300px', marginTop: '20px', width: '100%' }}>
            <Bar data={chartData} options={chartOptions} />
          </div>

          <Typography variant="body1" style={{ marginTop: '20px' }}>
            <strong>Most Recently Approved:</strong> {applicationData.most_recently_approved_application}
          </Typography>
          <Typography variant="body1">
            <strong>Top Representative:</strong> {applicationData.top_representative}
          </Typography>
          <Typography variant="body1">
            <strong>Top Application State:</strong> {applicationData.top_application_state}
          </Typography>
          <Typography variant="body1">
            <strong>Most Common Contract Type:</strong> {applicationData.most_common_contract_type}
          </Typography>

          <div className="card-stat-summary--meta">
            <div className="card-stat-summary--meta-timeline">{`Updated: ${updateDate.toLocaleTimeString()}`}</div>
          </div>
        </div>
        <div className="card-bg">
          <img src={fileContractIcon} alt="File Contract Icon" className="bg-icon" />
        </div>
      </div>
    </Grid>
  );
};
